import { CSSProperties, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { TableHeader, TableSort } from '..'
import Icon from '../../Icon'
import { ICON } from '../../Icon/data'
import { COLOR } from '../../../data/variables'
import { useTranslation } from 'react-i18next'
import Tooltip from '../../Tooltip'

type Row = {
  isHeader?: boolean
  data?: Record<string, TableHeader>
  header?: Record<string, TableHeader>
  rowHeight?: number
  id?: number
  isFiller?: boolean
  autoExpand?: boolean
  isLoading?: boolean
  onRowClick?: (row: any) => void
  sort?: TableSort | null
  onSort?: (key: string) => void
}

const Row = memo(({
  isHeader = false, data = {}, header = {}, rowHeight, id, isFiller = false,
  autoExpand = true, isLoading = false, onRowClick, sort, onSort = () => {}
}: Row) => {
  const { t } = useTranslation()

  const [expanded, setExanded] = useState<boolean>(data?.isExpanded ?? false)

  const columnTag = useCallback((children: ReactNode, index: number, key: string) => {
    const { isPinned: pinned, width, disableSort } = header[key]
    const isPinned = pinned ?? false

    const style: CSSProperties = { height: `${rowHeight}px` }
    if (width) {
      if (typeof width === 'string') {
        style.width = width
        style.width = width
        style.maxWidth = width
      } else {
        style.minWidth = width.min
        style.maxWidth = width.max
      }
    }

    const colClass = [key, ...header[key].class ?? []]
    if (isPinned) colClass.push('pinned')
    if (isHeader) {
      const headerClass = []
      const hasSort = typeof disableSort === 'undefined' ? true : !disableSort
      if (hasSort) {
        headerClass.push('sortable')
        colClass.push('clickable')
      }
      const hasActiveSort = key === sort?.column
      let sortIcon = ICON.ARROW_UP_DOWN
      if (hasActiveSort) {
        sortIcon = sort.type === 'ascending' ? ICON.ARROW_UP : ICON.ARROW_DOWN
        headerClass.push('active')
      }

      const sortElem = hasSort ? <Icon icon={sortIcon} size={16} /> : null
      return (
        <th
          className={colClass.join(' ')}
          role="button"
          tabIndex={0}
          onClick={() => hasSort ? onSort(key) : {}}
          style={style}
        >
          <div
            className={`Row__header-col${headerClass.length ? ` ${headerClass.join(' ')}` : ''}`}
          >
            <Tooltip content={children} text={children} />
            {sortElem}
          </div>
        </th>
      )
    }

    return (
      <td
        key={`col_${isHeader ? 'head' : `${id}_${index}`}`}
        className={colClass.join(' ')}
        style={style}
      >
        {children}
      </td>
    )
  }, [isHeader, id, expanded, header, sort, onSort, rowHeight])

  const styles = useMemo(() => {
    const lookup = {}
    if (!isFiller) {
      lookup.height = `${rowHeight}px`
    }
    return lookup
  }, [rowHeight, isFiller])

  const classes = useMemo(() => {
    const classArr = []
    if (isFiller) classArr.push('filler')
    if (header?.EXPAND && !isHeader) classArr.push('expandable')
    if (expanded) classArr.push('expanded')
    return classArr
  }, [isFiller, expanded, header])

  const onClick = useCallback(() => {
    if (header?.EXPAND && (expanded || autoExpand)) {
      setExanded(!expanded)
    }
    if (onRowClick) onRowClick(data)
  }, [header, expanded, autoExpand, data, onRowClick])

  useEffect(() => {
    setExanded(data.isExpanded ?? false)
  }, [data.isExpanded])

  return (
    <>
      <tr
        key={`row_${isHeader ? 'head' : id}`}
        style={styles}
        className={classes.join(' ')}
        onClick={() => onClick()}
      >
        {Object.keys(header).sort((a, b) => header[a]?.index > header[b]?.index ? 1 : -1).map((key, index) => {
          const value = isHeader ? data[key]?.label ?? '' : data[key]
          if (!isHeader && header[key].formatFunction) {
            // Disable formatFunction if table is loading
            const { formatFunction } = !isLoading ? header[key] : { formatFunction: (val) => val }
            return (
              <>
                {columnTag(formatFunction(value, data), index, key)}
              </>
            )
          } else if (key === 'EXPAND' && !isHeader) {
            return (
              <>
                {columnTag(
                  <Icon
                    icon={expanded ? ICON.CHEVRON_UP : ICON.CHEVRON_DOWN}
                    color={expanded ? COLOR.WHITE : COLOR.TEXT_GRAY}  
                  />, index, key)}
              </>
            )
          } else if (isHeader && header[key].headerFormatFunction) {
            const { headerFormatFunction } = header[key]
            return (
              <>
                {columnTag(headerFormatFunction(value, data), index, key)}
              </>
            )
          }

          return (
            <>
              {columnTag(value, index, key)}
            </>
          )
        })}
      </tr>
      {expanded && !isHeader &&
        <tr className="Row__expanded">
          <td />
          <td
            className="Row__expanded-content"
            colSpan={Object.keys(header).length - 1}
          >
            {data.expandElem ?? t('No data to show')}
          </td>
        </tr>
      }
    </>
  )
})

export default Row
